import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { Dialog } from './ui/dialog';

// Terminal-style command palette (⌘K / Ctrl+K). Controlled by the parent.
export default function CommandPalette({ open, onClose, commands }) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    setQ('');
    setActive(0);
    const id = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [open]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return commands.filter((c) => !s || c.label.toLowerCase().includes(s) || (c.id || '').includes(s));
  }, [commands, q]);

  useEffect(() => { setActive(0); }, [q]);

  const run = (c) => {
    if (!c) return;
    onClose();
    c.run();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); run(filtered[active]); }
  };

  return (
    <Dialog open={open} onClose={onClose} className="max-w-xl">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <span className="text-primary">$</span>
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="type a command…"
          className="w-full bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
        />
        <kbd className="rounded border border-border px-1.5 text-[10px] text-muted-foreground">esc</kbd>
      </div>
      <ul className="max-h-72 overflow-y-auto py-1">
        {filtered.length === 0 ? (
          <li className="px-4 py-3 text-sm text-muted-foreground">no matches</li>
        ) : (
          filtered.map((c, i) => (
            <li key={c.id}>
              <button
                type="button"
                onMouseEnter={() => setActive(i)}
                onClick={() => run(c)}
                className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors ${
                  i === active ? 'bg-accent text-foreground' : 'text-muted-foreground'
                }`}
              >
                <ChevronRight className={`h-3.5 w-3.5 ${i === active ? 'text-primary' : 'opacity-0'}`} />
                <span className="flex-1">{c.label}</span>
                {c.hint ? <kbd className="rounded border border-border px-1.5 text-[10px]">{c.hint}</kbd> : null}
              </button>
            </li>
          ))
        )}
      </ul>
    </Dialog>
  );
}
